/**
 * Admin waitlist for a capped program (BHCS-124), shown in the expanded program row.
 *
 * The admin decides who gets a seat. Offering one holds it for that child (it counts against
 * the cap for everyone else) and emails the family; they enroll themselves, and the entry
 * resolves on its own. "Next in line" marks the head of the queue, but any waiting child can be
 * offered (siblings, returning families). Offering with no seat free is allowed and asks first,
 * matching how admins can already enroll past the cap.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { trackEvent } from '../../utils/analytics';
import { formatDateInBusinessTZ } from '../../utils/date';
import { PROGRAM_CAPACITY_KEY } from '../../hooks/useProgramSeats';
import {
  describeSeats,
  fetchProgramCapacity,
  fetchProgramWaitlist,
  isProgramFull,
  offerWaitlistSeat,
  rescindWaitlistOffer,
  withdrawFromWaitlist,
  type AdminWaitlistEntry,
  type ProgramCapacityStatus,
} from '../../services/programWaitlist';

const WAITLIST_KEY = 'admin-program-waitlist';

/** "8 / 10 seats · 3 waiting", or "Full 10 / 10" — for the program list. Nothing when uncapped. */
export function ProgramSeatsBadge({ status }: { status: ProgramCapacityStatus | undefined }) {
  if (!status) return null;
  const full = isProgramFull(status);
  return (
    <span
      className={`px-2 py-0.5 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${
        full ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
      }`}
      title={`${status.seats_taken} enrolled, ${status.seats_held} held for a waitlist offer, capacity ${status.capacity}`}
    >
      {full ? `Full ${describeSeats(status)}` : `${describeSeats(status)} seats`}
      {status.waitlist_count > 0 && ` · ${status.waitlist_count} waiting`}
    </span>
  );
}

function Stat({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'default' | 'warn' }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-base font-semibold leading-tight ${tone === 'warn' ? 'text-amber-700' : 'text-gray-900'}`}>
        {value}
      </div>
    </div>
  );
}

type PendingAction = { entryId: string; kind: 'offer' | 'remove' } | null;

export function ProgramWaitlistPanel({ program }: { program: { id: string; name: string; capacity?: number | null } }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [confirming, setConfirming] = useState<PendingAction>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const { data: statusById } = useQuery({
    queryKey: [PROGRAM_CAPACITY_KEY, program.id],
    queryFn: () => fetchProgramCapacity([program.id]),
  });
  const status = statusById?.get(program.id);

  const { data: entries, isLoading, error } = useQuery({
    queryKey: [WAITLIST_KEY, program.id],
    queryFn: () => fetchProgramWaitlist(program.id),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: [PROGRAM_CAPACITY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [WAITLIST_KEY, program.id] });
  };

  const track = (eventType: string, entry: AdminWaitlistEntry, extra: Record<string, unknown> = {}) => {
    trackEvent({
      eventType,
      user,
      userType: 'admin',
      entityType: 'program_waitlist',
      entityId: entry.id,
      metadata: { program_id: program.id, program_name: program.name, student_id: entry.student_id, ...extra },
      email: user?.email,
    });
  };

  const childName = (entry: AdminWaitlistEntry) =>
    entry.students ? `${entry.students.first_name} ${entry.students.last_name}`.trim() : 'Unknown student';

  const action = useMutation({
    mutationFn: async ({ kind, entry }: { kind: 'offer' | 'rescind' | 'remove'; entry: AdminWaitlistEntry }) => {
      if (kind === 'offer') return { kind, entry, ...(await offerWaitlistSeat(entry.id)) };
      if (kind === 'rescind') await rescindWaitlistOffer(entry.id);
      else await withdrawFromWaitlist(entry.id);
      return { kind, entry, over_capacity: false };
    },
    onSuccess: ({ kind, entry, over_capacity }) => {
      setConfirming(null);
      const name = childName(entry);
      if (kind === 'offer') {
        track('admin_waitlist_offer', entry, { over_capacity });
        setNotice({
          tone: 'ok',
          text: over_capacity
            ? `Seat offered to ${name}, and the family has been emailed. ${program.name} is over capacity until someone leaves.`
            : `Seat offered to ${name}, and the family has been emailed. It is held until they enroll or you take it back.`,
        });
      } else if (kind === 'rescind') {
        track('admin_waitlist_rescind', entry);
        setNotice({ tone: 'ok', text: `Offer to ${name} taken back. They are back in their place in line.` });
      } else {
        track('admin_waitlist_remove', entry);
        setNotice({ tone: 'ok', text: `${name} removed from the waitlist.` });
      }
      refresh();
    },
    onError: (err: Error) => {
      setConfirming(null);
      setNotice({ tone: 'error', text: err.message });
      refresh();
    },
  });

  const live = entries ?? [];
  const waiting = live.filter((e) => e.status === 'waiting');
  const headId = waiting[0]?.id;
  const noSeatFree = isProgramFull(status);

  const onOffer = (entry: AdminWaitlistEntry) => {
    if (noSeatFree && !(confirming?.entryId === entry.id && confirming.kind === 'offer')) {
      setConfirming({ entryId: entry.id, kind: 'offer' });
      return;
    }
    action.mutate({ kind: 'offer', entry });
  };

  const onRemove = (entry: AdminWaitlistEntry) => {
    if (!(confirming?.entryId === entry.id && confirming.kind === 'remove')) {
      setConfirming({ entryId: entry.id, kind: 'remove' });
      return;
    }
    action.mutate({ kind: 'remove', entry });
  };

  const button = 'inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border transition-colors disabled:opacity-50';

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2" data-testid="program-waitlist-panel">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-700">Capacity &amp; waitlist</div>

      {status && (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
          <Stat label="Capacity" value={`${status.capacity}`} />
          <Stat label="Enrolled" value={`${status.seats_taken}`} tone={status.seats_taken > status.capacity ? 'warn' : 'default'} />
          <Stat label="Held" value={`${status.seats_held}`} />
          <Stat label="Seats left" value={`${status.seats_left}`} tone={noSeatFree ? 'warn' : 'default'} />
          <Stat label="Waiting" value={`${status.waitlist_count}`} />
        </div>
      )}

      {notice && (
        <p className={`mb-2 text-xs ${notice.tone === 'ok' ? 'text-green-700' : 'text-red-600'}`} role={notice.tone === 'error' ? 'alert' : 'status'}>
          {notice.text}
        </p>
      )}

      {isLoading && <div className="text-xs text-gray-500">Loading waitlist…</div>}
      {error && <div className="text-xs text-red-600">Failed to load the waitlist</div>}
      {!isLoading && !error && live.length === 0 && (
        <div className="text-xs text-gray-500">Nobody is waiting.</div>
      )}

      {live.length > 0 && (
        <ol className="divide-y divide-gray-100">
          {live.map((entry) => {
            const position = entry.status === 'waiting' ? waiting.findIndex((w) => w.id === entry.id) + 1 : null;
            const askingOffer = confirming?.entryId === entry.id && confirming.kind === 'offer';
            const askingRemove = confirming?.entryId === entry.id && confirming.kind === 'remove';
            return (
              <li key={entry.id} className="flex flex-wrap items-start justify-between gap-2 py-2">
                <div className="min-w-0 text-xs">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-semibold text-gray-900">{childName(entry)}</span>
                    {entry.status === 'offered' ? (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800">
                        Seat held · offered {entry.offered_at ? formatDateInBusinessTZ(entry.offered_at) : ''}
                      </span>
                    ) : (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                        #{position}{entry.id === headId ? ' · next in line' : ''}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-gray-500">
                    {entry.parents
                      ? `${[entry.parents.first_name, entry.parents.last_name].filter(Boolean).join(' ')} · ${entry.parents.email}`
                      : 'Parent unknown'}
                    {' · joined '}
                    {formatDateInBusinessTZ(entry.created_at)}
                    {entry.status === 'offered' && entry.offered_by ? ` · by ${entry.offered_by}` : ''}
                  </div>
                  {askingOffer && (
                    <div className="mt-1 text-amber-700">No seat is free. Offering puts {program.name} over capacity. Offer anyway?</div>
                  )}
                  {askingRemove && (
                    <div className="mt-1 text-amber-700">Remove {childName(entry)} from the line? They would lose their place.</div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {entry.status === 'waiting' && (
                    <button
                      type="button"
                      className={`${button} text-green-700 bg-green-50 border-green-200 hover:bg-green-100`}
                      onClick={() => onOffer(entry)}
                      disabled={action.isPending}
                    >
                      {askingOffer ? 'Offer anyway' : 'Offer seat'}
                    </button>
                  )}
                  {entry.status === 'offered' && (
                    <button
                      type="button"
                      className={`${button} text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100`}
                      onClick={() => action.mutate({ kind: 'rescind', entry })}
                      disabled={action.isPending}
                    >
                      Take back offer
                    </button>
                  )}
                  <button
                    type="button"
                    className={`${button} text-red-600 bg-red-50 border-red-200 hover:bg-red-100`}
                    onClick={() => onRemove(entry)}
                    disabled={action.isPending}
                  >
                    {askingRemove ? 'Yes, remove' : 'Remove'}
                  </button>
                  {(askingOffer || askingRemove) && (
                    <button
                      type="button"
                      className={`${button} text-gray-600 bg-white border-gray-200 hover:bg-gray-50`}
                      onClick={() => setConfirming(null)}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
